'use client';

import type { FragmentType } from '@packages/gql/generated/shopping';
import { useAbTesting } from '@packages/shared/src/abtesting/useAbTesting/useAbTesting';
import { getFragmentData } from '@packages/gql/generated/shopping';
import type { GtmProduct } from '@packages/tracking/src/hooks/useTracking/types';
import { PageMetaData } from '@packages/modules/src/PageMetaData/PageMetaData';
import { useEffect } from 'react';
import { TrackingBreadcrumbsFragmentFragmentDoc } from '@packages/gql/generated/shopping/TrackingBreadcrumbsFragmentFragmentDoc';
import { TrackingFragmentFragmentDoc } from '@packages/gql/generated/shopping/TrackingFragmentFragmentDoc';
import { getCategoryData } from './helper/getCategoryData';
import { useDefaultTracking } from './hooks/useDefaultTracking';
import { useAddLastSeen } from '../../hooks/useAddLastSeen';

/* GraphQL */ `
  fragment TrackingFragment on Product {
    akl
    sku
    ...UseAddLastSeenFragment
    ...GetSizeAndVariantFromDimensionsFragment
    availability {
      ...GetDeterminedAvailabilityFragment
    }
    ...UseDefaultTrackingFragment
    ...GetGlycerinDisplayProductPayloadFragment
  }
`;

/* GraphQL */ `
  fragment TrackingBreadcrumbsFragment on Breadcrumb {
    ...GetCategoryDataFragment
  }
`;

export type TrackingProps = {
  breadCrumbData: FragmentType<typeof TrackingBreadcrumbsFragmentFragmentDoc> | undefined | null;
  productData: FragmentType<typeof TrackingFragmentFragmentDoc>;
  navq?: GtmProduct['category'];
};

/**
 * Tracking component used to track
 * */
export const Tracking = ({ navq, productData, breadCrumbData }: TrackingProps) => {
  const { setDefaultViewOutcome } = useAbTesting();

  const unmaskedData = getFragmentData(TrackingFragmentFragmentDoc, productData);

  const { akl, sku } = unmaskedData;

  useAddLastSeen(unmaskedData);

  const { categoryPath, categoryPathAsString } = getCategoryData(
    getFragmentData(TrackingBreadcrumbsFragmentFragmentDoc, breadCrumbData),
  );

  const { trackGtmProductView, trackGlycerinDisplayProduct, trackGtmPageNavigation } =
    useDefaultTracking();

  useEffect(() => {
    trackGtmProductView({ data: unmaskedData, navq, categoryPathAsString });
    // Glycerin Stuff
    // product event for glycerin tracking
    trackGlycerinDisplayProduct(unmaskedData, categoryPath);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sku]);

  /* PDVPageNavigation event: new product loaded */
  useEffect(() => {
    if (akl) {
      trackGtmPageNavigation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [akl]);

  // lackmus framework default view tracking for abtesting
  useEffect(() => {
    setDefaultViewOutcome(sku, akl, 'RPP');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [akl, sku]);

  return (
    <PageMetaData
      page={{ type: 'detailview', template: 'detailview' }}
      categoryPath={categoryPath}
    />
  );
};
